import {
  Box,
  VStack,
  Text,
  Textarea,
  HStack,
  Icon,
  Flex,
  Center,
  keyframes,
} from '@chakra-ui/react'
import GlobalButton from '@components/GlobalButton/GlobalButton'
import {ReactComponent as CheckIcon} from '@assets/svgs/learn/check.svg'
import {ReactComponent as MicIcon} from '@assets/svgs/mic_2.svg'
import {useEffect, useState, useRef} from 'react'
import learnPartTwoRequest from '@apis/learn-part-two'
import {Popover} from 'antd'
import {audioRef} from '@utils'

// Add this interface at the top of your file, before the component

const slideUp = keyframes`
from { transform: translateY(20px); opacity: 0; }
to { transform: translateY(0); opacity: 1; }
`

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`

const rippleAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(2.5); opacity: 0; }
`

const MakeOutline = ({
  currentContent,
  sessionId,
  setIsShowFooter,
}: {
  currentContent: any
  sessionId: string
  setIsShowFooter: any
}) => {
  const animation = `${slideUp} 0.5s ease-out`
  const [answer, setAnswer] = useState<any>('')
  const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false)
  const [isShowInput, setIsShowInput] = useState<boolean>(true)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const recognitionRef = useRef<any>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)
  const [tempTranscript, setTempTranscript] = useState<string>('')

  useEffect(() => {
    setAnswer('')
    setTempTranscript('')
    setIsShowInput(true)
    setIsShowFooter(false)

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition()
      recognitionRef.current.continuous = true
      recognitionRef.current.interimResults = true
      recognitionRef.current.lang = 'en-US'

      recognitionRef.current.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0])
          .map(result => result.transcript)
          .join('')

        if (event.results[event.results.length - 1].isFinal) {
          setTempTranscript(transcript)
        }
      }

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error)
        setIsRecording(false)
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [currentContent])

  const handleSubmit = async () => {
    setIsLoadingBtn(true)
    const answers = answer.split('\n').filter((item: any) => item.trim() !== '')
    try {
      const res = await learnPartTwoRequest.submitOutline({
        session_id: sessionId,
        phase: `${currentContent?.phase}`,
        content: answers,
      })
      const params = {
        session_id: sessionId,
        step: currentContent?.step,
      }
      learnPartTwoRequest.saveStep(params)
      setAnswer(answers)
      setIsShowInput(false)
      setIsShowFooter(true)
    } catch (error) {
    } finally {
      setIsLoadingBtn(false)
    }
  }

  const handleChangeAnswer = (e: any) => {
    const text = e.target.value
    setAnswer(text)
  }

  const startRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    try {
      recognitionRef.current?.start()
      setIsRecording(true)
      setIsPopoverOpen(true)
    } catch (error) {
      console.error('Error starting speech recognition:', error)
    }
  }

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      setIsRecording(false)
      setIsPopoverOpen(false)
      if (tempTranscript) {
        setAnswer((prev: any) =>
          prev ? `${prev}\n${tempTranscript}` : tempTranscript,
        )
        setTempTranscript('')
      }
    }
  }

  return (
    <HStack align="flex-start" spacing={8} p={6} animation={animation}>
      {/* Left side - Background section */}
      <Box flex={1}>
        <Text fontWeight="600" mb={2} fontSize={'16px'}>
          {currentContent?.phase}
        </Text>
        <Text mb={4} color="#475467" fontSize={'16px'}>
          {currentContent?.data?.phase_description}
        </Text>

        <VStack align="flex-start" spacing={4}>
          {currentContent?.data?.phase_questions?.map(
            (question: any, index: number) => (
              <Flex key={index} alignItems={'flex-start'} gap={2}>
                <Center
                  w={'24px'}
                  h={'24px'}
                  bg={'#D6EBFF'}
                  borderRadius={'50%'}>
                  <Icon as={CheckIcon} />
                </Center>
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize={'16px'} color={'#475467'}>
                    {question.text_en}
                  </Text>
                  <Text fontSize="16px" color="#98A2B3">
                    {question.text_vi}
                  </Text>
                </VStack>
              </Flex>
            ),
          )}
        </VStack>
      </Box>

      {/* Right side - Text input section */}
      <Box
        flex={1}
        pl={isShowInput ? 0 : 8}
        borderLeft={isShowInput ? 'none' : '1px solid #E4E7EC'}
        h={isShowInput ? 'auto' : '100%'}>
        {isShowInput ? (
          <>
            <Text mb={2} fontWeight={500} color="#344054" fontSize={'16px'}>
              Trả lời theo ý ở bên hoặc tự viết theo ý bạn{' '}
              <Text as="span" color="#1890FF">
                *
              </Text>
            </Text>
            <Box position="relative">
              <Textarea
                placeholder="Nhập ý tưởng tại đây"
                height="200px"
                mb={2}
                value={answer}
                onChange={handleChangeAnswer}
              />
              <Box position="relative">
                {isRecording && (
                  <Box
                    position="absolute"
                    bottom="4"
                    right="4"
                    width="20px"
                    height="20px"
                    borderRadius="50%"
                    bg="#FF4D4F"
                    opacity="0.3"
                    animation={`${rippleAnimation} 1.5s infinite`}
                  />
                )}
                <Popover
                  content={
                    isRecording ? 'Bấm để dừng ghi âm' : 'Bấm để bắt đầu ghi âm'
                  }
                  trigger="hover"
                  placement="top"
                  open={isRecording || isPopoverOpen}
                  onOpenChange={visible =>
                    !isRecording && setIsPopoverOpen(visible)
                  }>
                  <Icon
                    as={MicIcon}
                    position="absolute"
                    bottom={4}
                    right={4}
                    w="20px"
                    h="20px"
                    cursor="pointer"
                    zIndex={10}
                    onClick={isRecording ? stopRecording : startRecording}
                    animation={
                      isRecording ? `${pulseAnimation} 1.5s infinite` : 'none'
                    }
                    sx={{
                      path: {fill: isRecording ? '#FF4D4F' : '#98A2B3'},
                    }}
                  />
                </Popover>
              </Box>
            </Box>
            <Flex justifyContent={'center'} mt={1}>
              <GlobalButton
                w={'100px'}
                onClick={handleSubmit}
                isDisabled={isLoadingBtn || answer.length === 0 || isRecording}>
                Gửi
              </GlobalButton>
            </Flex>
          </>
        ) : (
          <>
            <Text fontWeight="600" mb={6} fontSize={'16px'}>
              Ý của bạn
            </Text>
            <VStack align="flex-start" spacing={4}>
              {answer?.map((item: any, index: number) => (
                <Flex key={index} alignItems={'flex-start'} gap={2}>
                  <Center
                    w={'24px'}
                    h={'24px'}
                    bg={'#D6EBFF'}
                    borderRadius={'50%'}>
                    <Icon as={CheckIcon} />
                  </Center>
                  <VStack align="flex-start" spacing={0}>
                    <Text fontSize={'16px'} color={'#475467'}>
                      {item}
                    </Text>
                  </VStack>
                </Flex>
              ))}
            </VStack>
          </>
        )}
      </Box>
    </HStack>
  )
}

export default MakeOutline
